import { uniqueId } from "lodash-es"
import { ChangeEvent, useState } from "react"
import { useTranslation } from "react-i18next"
import { asyncDirectUpload } from "./asyncDirectUpload"
import {
  UseFileUploadComponentConfig,
  FilePayload,
  UseFileUploadComponentReturnType,
} from "./types"
import { FileStatus, isFileOverSize, URL } from "./utils"
import { gql, useMutation } from "@apollo/client"
import { PurgeUnattachedFileInput, PurgeUnattachedFilePayload } from "@oysterhr/schema"

export const FILE_UPLOAD_SHARED_PURGE_UNATTACHED_FILE = gql`
  mutation FileUploadSharedPurgeUnattachedFile($signedId: String!) {
    purgeUnattachedFile(input: { signedId: $signedId }) {
      errors
    }
  }
`

/**
 * This hooks is meant to be used with `FileUpload` component from the UI library (@oysterhr/ui).
 * @see https://storybook.oysterhr.com/?path=/docs/design-system-form-components-file-upload-docs--page
 */
export function useFileUploadComponent(
  config?: UseFileUploadComponentConfig,
  initialFiles: FilePayload[] = [],
): UseFileUploadComponentReturnType {
  const { t } = useTranslation("shared/hooks/useFileUpload")
  const [files, setFiles] = useState<FilePayload[]>(initialFiles)

  const [handleFilePurge] = useMutation<
    { purgeUnattachedFile: PurgeUnattachedFilePayload },
    PurgeUnattachedFileInput
  >(FILE_UPLOAD_SHARED_PURGE_UNATTACHED_FILE)

  const handleChange =
    (callback?: (result: FilePayload[]) => void) =>
    async (event: ChangeEvent<HTMLInputElement>) => {
      const { files: fileList } = event.target

      if (fileList?.length) {
        const files = Array.from(fileList).map(getFilePayload)
        setFiles(prev => (config?.multiple ? [...prev, ...files] : [...files]))

        const uploads: Promise<FilePayload>[] = files
          .filter(({ status }) => status === FileStatus.Idle)
          .map(uploadFile)
        const result = await Promise.all(uploads)

        callback?.(result)
      }
    }

  const getFilePayload = (file: File) => {
    const fileExtension = file.name.split(".").pop()?.toLowerCase()
    const isValidExtension = config?.acceptedExtensions?.length
      ? config.acceptedExtensions.includes(fileExtension || "")
      : true
    const base = { id: uniqueId(), file, status: FileStatus.Idle, error: undefined }

    if (!isValidExtension) {
      return {
        ...base,
        status: FileStatus.Error,
        error: t("fileTypeError"),
      }
    }

    if (config?.maxSize && isFileOverSize(file.size, config.maxSize)) {
      return {
        ...base,
        status: FileStatus.Error,
        error: t("fileSizeError"),
      }
    }

    return base
  }

  const uploadFile = async ({ file, ...rest }: FilePayload) => {
    try {
      const blob = await asyncDirectUpload(file, URL)
      const newFile = {
        ...rest,
        file,
        signedId: blob.signed_id,
        status: FileStatus.Uploaded,
        href: window.URL.createObjectURL(file),
      }
      setFiles(prev => prev.map(f => (f.id === newFile.id ? newFile : f)))
      return newFile
    } catch {
      const newFile = { ...rest, file, status: FileStatus.Error, error: t("networkError") }
      setFiles(prev => prev.map(f => (f.id === newFile.id ? newFile : f)))
      return newFile
    }
  }

  /**
   * The file is removed from the UI regardless of the outcome of the API to purge
   * the file from the DB. This is simplified, at the moment, because if we want to
   * handle the loading, success, and error states, the `useFileUploadComponent` hook
   * API would be modified and need to be updated in places where it has been used.
   *
   * TODO: Add `isRemovingFile` field to the return type of the `useFileUploadComponent` hook.
   */
  const handleRemoveFile =
    (callback?: (result: FilePayload[]) => void) => (id: FilePayload["id"]) => {
      let fileToBeRemoved: undefined | FilePayload
      const newFiles: FilePayload[] = []

      files.forEach(file => {
        if (file.id === id) {
          fileToBeRemoved = file
        } else {
          newFiles.push(file)
        }
      })

      if (fileToBeRemoved?.signedId) {
        handleFilePurge({ variables: { signedId: fileToBeRemoved.signedId } })
      }

      setFiles(newFiles)
      callback?.(newFiles)
    }

  return {
    files,
    handleChange,
    handleRemoveFile,
  }
}
